import { keepPreviousData, type UseQueryResult, useQuery } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { ClientStatsService } from '../../services/consultant/client-stats.service';
import type {
  AlertFeedLane,
  CallRoundStateTab,
  ClientStatsAlertsResponse,
  ClientStatsPortfolioResponse,
  ClientStatsPreset,
  ClientStatsSeriesResponse,
  ClientStatsSortBy,
  ClientStatsSortOrder,
  ClientStatsSummary,
  IClientStatsService,
} from '../../services/consultant/client-stats.service.interface';

export const CLIENT_STATS_PAGE_SIZE = 50;

export interface ConsultantClientStatsFilters {
  search: string;
  sortBy?: ClientStatsSortBy;
  sortOrder?: ClientStatsSortOrder;
}

const EMPTY_SUMMARY: ClientStatsSummary = { critical: 0, warning: 0, ok: 0, na: 0 };

export const clientStatsQueryKeys = {
  all: ['consultant', 'client-stats'] as const,
  portfolio: (
    preset: ClientStatsPreset,
    page: number,
    pageSize: number,
    filters?: ConsultantClientStatsFilters,
  ) =>
    [
      'consultant',
      'client-stats',
      'portfolio',
      preset,
      page,
      pageSize,
      filters?.search.trim() || null,
      filters?.sortBy ?? null,
      filters?.sortOrder ?? null,
    ] as const,
  /**
   * La lane È parte della chiave (T4.4): operational e intangible sono due
   * feed diversi con totali propri, mai cache condivisa.
   */
  alerts: (preset: ClientStatsPreset, lane: AlertFeedLane = 'operational') =>
    ['consultant', 'client-stats', 'alerts', preset, lane] as const,
  series: (tenantId: string, preset: ClientStatsPreset) =>
    ['consultant', 'client-stats', 'series', tenantId, preset] as const,
  /** Sotto la radice `all`: le invalidate esistenti (esiti/titolare) la coprono già. */
  callRound: (
    preset: ClientStatsPreset,
    state: CallRoundStateTab,
    page: number,
    search: string | null,
  ) => ['consultant', 'client-stats', 'call-round', preset, state, page, search] as const,
  clientAlerts: (tenantId: string, preset: ClientStatsPreset, includeClosed: boolean) =>
    ['consultant', 'client-stats', 'client-alerts', tenantId, preset, includeClosed] as const,
};

/**
 * Query portfolio: paginata + filtri URL. keepPreviousData = zero flash tra
 * pagine/preset. `enabled` disattiva la query quando la pagina è sul tab
 * Avvisi (giro di chiamate, `useConsultantCallRound`): un solo fetch attivo.
 */
export function useConsultantClientStats(
  preset: ClientStatsPreset,
  page: number,
  filters: ConsultantClientStatsFilters = { search: '' },
  pageSize = CLIENT_STATS_PAGE_SIZE,
  enabled = true,
) {
  const clientStatsService = useInjection<IClientStatsService>(ClientStatsService);

  const query: UseQueryResult<ClientStatsPortfolioResponse> = useQuery({
    queryKey: clientStatsQueryKeys.portfolio(preset, page, pageSize, filters),
    queryFn: () => {
      const term = filters.search.trim();
      return clientStatsService.getPortfolio({
        preset,
        page,
        pageSize,
        search: term || undefined,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      });
    },
    placeholderData: keepPreviousData,
    enabled,
  });

  return {
    portfolio: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    pageSize: query.data?.pageSize ?? pageSize,
    summary: query.data?.summary ?? EMPTY_SUMMARY,
    range: query.data ? { from: query.data.from, to: query.data.to } : null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
  };
}

/**
 * Feed alert "Da richiamare" (cap 500 server-side, `total` = conteggio pre-cap).
 * `lane` seleziona la corsia (T4.4): operational = coda giornaliera (default),
 * intangible = "da verificare" (le card INTANGIBLE delle regole D/E vivono lì).
 */
export function useConsultantClientStatsAlerts(
  preset: ClientStatsPreset,
  lane: AlertFeedLane = 'operational',
) {
  const clientStatsService = useInjection<IClientStatsService>(ClientStatsService);

  const query: UseQueryResult<ClientStatsAlertsResponse> = useQuery({
    queryKey: clientStatsQueryKeys.alerts(preset, lane),
    queryFn: () => clientStatsService.getAlerts(preset, lane),
    placeholderData: keepPreviousData,
  });

  return {
    alerts: query.data?.alerts ?? [],
    total: query.data?.total ?? 0,
    /** Clienti distinti da richiamare (T0.6b), non il numero di alert. */
    tenantsTotal: query.data?.tenantsTotal ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}

/** Serie per il dettaglio tenant; disabled senza tenantId (route figlia opzionale). */
export function useConsultantClientStatsSeries(tenantId: string | null, preset: ClientStatsPreset) {
  const clientStatsService = useInjection<IClientStatsService>(ClientStatsService);

  const query: UseQueryResult<ClientStatsSeriesResponse | null> = useQuery({
    queryKey: tenantId
      ? clientStatsQueryKeys.series(tenantId, preset)
      : ['consultant', 'client-stats', 'series', 'none'],
    queryFn: () => clientStatsService.getSeries(tenantId as string, preset),
    enabled: Boolean(tenantId),
    placeholderData: keepPreviousData,
  });

  return {
    series: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
